import { useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from '@/design/components';

/**
 * חתימה באצבע על המסך (PRD 5.10.1).
 *
 * קנבס ולא ספרייה: ציור קו בין שתי נקודות מגע הוא כל מה שצריך כאן,
 * וספרייה הייתה מוסיפה משקל לחבילה בשביל זה.
 *
 * pointer events ולא touch ו mouse בנפרד: אותו קוד עובד באצבע,
 * בעכבר ובעט, וזה מה שדפדפנים תומכים בו היום.
 */
export function SignaturePad({
  onChange,
  disabled,
}: {
  onChange: (blob: Blob | null) => void;
  disabled?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const context = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    // גודל הקנבס בפיקסלים אמיתיים, אחרת החתימה יוצאת מטושטשת
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = canvas.clientWidth;
    if (canvas.width !== Math.round(width * ratio)) {
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(160 * ratio);
      const fresh = canvas.getContext('2d');
      if (fresh) {
        fresh.scale(ratio, ratio);
        fresh.lineWidth = 2.5;
        fresh.lineCap = 'round';
        fresh.lineJoin = 'round';
        fresh.strokeStyle = '#111111';
      }
      return fresh;
    }
    return canvas.getContext('2d');
  };

  const pointAt = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const ctx = context();
    if (!ctx) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    const { x, y } = pointAt(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = context();
    if (!ctx) return;
    const { x, y } = pointAt(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasInk) setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    emit();
  };

  const emit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => onChange(blob), 'image/png');
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div className="grid gap-2">
      <canvas
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        aria-label="אזור החתימה"
        // touch-none, אחרת הדפדפן גולל את הדף במקום לצייר
        className="h-40 w-full touch-none rounded-sm border border-dashed border-muted bg-surface"
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" disabled={!hasInk || disabled} onClick={clear}>
          <Eraser size={18} strokeWidth={1.75} aria-hidden="true" />
          לנקות
        </Button>
        <span className="text-label text-muted">
          {hasInk ? 'אפשר לחתום' : 'חותמים באצבע בתוך המסגרת'}
        </span>
      </div>
    </div>
  );
}
